import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter } from 'rxjs';
import { DialogAnimationService } from 'src/app/angular-core/services/dialog-animation.service';
import { SaveSheetService } from 'src/app/angular-core/services/save-sheet.service';
import { BasicXpacInfo, Company, CompanyType, DeviceService, XpacCompanyInfo } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { PermissionsService } from 'src/app/services/permissions.service';
import { CompanyFilter, CompanyFilterComponent } from 'src/app/shared/components/company-filter/company-filter.component';
import { errorEventAction } from 'src/app/store/actions/app.actions';
import { DevicesActionsTypes, getDeviceCompaniesAction, refreshDevicesAction, setDeviceCompaniesAction, setStoreInfoAction } from 'src/app/store/actions/devices.actions';
import { selectCompanyInfo } from 'src/app/store/selectors/devices.selectors';
import { OperationEnum } from 'src/app/utils/enums/operation.enums';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { toISODateOnly } from 'src/app/utils/functions/date.functions';
import { createReference, checkFormPristine, updateSaveSheetMultiple } from 'src/app/utils/functions/form.functions';
import { hasPermission } from 'src/app/utils/functions/permission.function';
import { IApiPermissions } from 'src/app/utils/interfaces/auth.interfaces';
import { IRemoteXPACPortalState, IXpacCompanyInfoState } from 'src/app/utils/interfaces/store.interfaces';
import { MatDialogRef } from '@angular/material/dialog';
import { MessageBoxCustomButton, MessageDialogComponent } from 'src/app/angular-core/components/message-dialog/message-dialog.component';

@UntilDestroy()
@Component({
  selector: 'xpac-xpac-details',
  templateUrl: './xpac-details.component.html',
  styleUrls: ['./xpac-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpacDetailsComponent implements OnInit {

  private static readonly MAX_LINKED_COMPANIES: number = 3;
  private retailerCompanyReference: Company;
  private partnerCompanyReference: Company;

  public isLoading: boolean = true;
  public formGroupXPACInfo: UntypedFormGroup;
  public formGroupCompanies: UntypedFormGroup;
  public formGroupPassword: UntypedFormGroup;
  public xpacCompanyInfo: XpacCompanyInfo;

  public partnerCompany: Company;
  public retailerCompany: Company;
  public linkedCompanies: Company[] = [];

  public searchText: string = '';
  @Input() public xpac: BasicXpacInfo = null;
  public hasPassword: boolean = false;
  public gettingPassword: boolean = false;

  private referenceFormGroupXPACInfo: object;
  private referenceFormGroupCompanies: object;

  private permissions: IApiPermissions;

  constructor(private store: Store<IRemoteXPACPortalState>,
    private saveSheetService: SaveSheetService,
    private translate: TranslateService,
    public dialog: DialogAnimationService,
    private formBuilder: UntypedFormBuilder,
    private actionsListener$: Actions,
    private permissionsService: PermissionsService,
    private cdr: ChangeDetectorRef,
    private api: DeviceService,
  ) { }

  public async ngOnInit(): Promise<void> {
    this.permissions = await this.permissionsService.getPermissions();
    this.initListeners();
    this.cdr.markForCheck();
  }

  public get CompanyType(): typeof CompanyType {
    return CompanyType;
  }

  public openCompaniesFilterDialog(companyType: CompanyType): void {

    let header: string = "";
    let filteredCompaniesTitle: string = this.translate.instant('Modules.Licenses.List.FilteredCompanies');

    if (companyType === CompanyType.Dealer && !this.canEditPartner()) {
      return;
    }

    if (companyType === CompanyType.Retailer && !this.canEditRetailer()) {
      return;
    }

    if (companyType === CompanyType.Installer && !this.canEditLinkedCompanies()) {
      return;
    }

    let availableTypes: CompanyType[];

    switch (companyType) {
      case CompanyType.Dealer: {
        header = this.translate.instant('Modules.Licenses.List.SelectPartnerHeader');
        filteredCompaniesTitle = this.translate.instant('Modules.Licenses.List.AvailablePartnerHeader');
        availableTypes = [companyType];
        break;
      }
      case CompanyType.Retailer: {
        header = this.translate.instant('Modules.Licenses.List.SelectRetailerHeader');
        filteredCompaniesTitle = this.translate.instant('Modules.Licenses.List.AvailableRetailerHeader');
        availableTypes = [companyType];
        break;
      }
      // CompanyType.Installer, this is for the LinkedCompanies section
      default: {
        header = this.translate.instant('Modules.Licenses.List.FilteredCompaniesHeader');
        availableTypes = [CompanyType.Installer, CompanyType.Dealer];
        break;
      }
    }

    const data: CompanyFilter = {
      header: header,
      filteredCompaniesTitle: filteredCompaniesTitle,
      companyTypes: availableTypes,
      excludedCompanies: companyType === CompanyType.Installer ? [...this.linkedCompanies, this.partnerCompany] : [],
      includeNoneOption: companyType === CompanyType.Retailer,
    };

    const dialogRef = this.dialog.open(CompanyFilterComponent, {
      panelClass: 'my-full-screen-dialog',
      data,
      animation: { to: "left" }
    });
    dialogRef.afterClosed().subscribe((filter: CompanyFilter) => {
      if (filter.selectedCompany) {
        switch (companyType) {
          case CompanyType.Dealer: {
            this.partnerCompany = filter.selectedCompany;
            this.formGroupCompanies.markAsDirty();
            this.formGroupCompanies.get("dealer").patchValue(this.getDescription(CompanyType.Dealer), { emitEvent: true });
            break;
          }
          case CompanyType.Retailer: {
            this.retailerCompany = filter.selectedCompany;
            this.formGroupCompanies.markAsDirty();
            this.formGroupCompanies.get("retailer").patchValue(this.getDescription(CompanyType.Retailer), { emitEvent: true });
            break;
          }
          case CompanyType.Installer: {
            if (this.linkedCompanies.length < XpacDetailsComponent.MAX_LINKED_COMPANIES) {
              this.linkedCompanies = [...this.linkedCompanies, filter.selectedCompany as Company];
              this.formGroupCompanies.markAsDirty();
              this.formGroupCompanies.get("installerCompanies").setValue(this.linkedCompanies, { emitEvent: true });
            }
            break;
          }
          default: {
            //statements; 
            break;
          }
        }
      }
      this.cdr.markForCheck();
    });
  }

  public getDescription(companyType: CompanyType): string {
    switch (companyType) {
      case CompanyType.Dealer: {
        if (this.partnerCompany !== null) {
          return this.partnerCompany.description !== "" ? this.partnerCompany.description : this.partnerCompany.companyId;
        }
      }
      case CompanyType.Retailer: {
        if (this.retailerCompany !== null) {
          return this.retailerCompany.description !== "" ? this.retailerCompany.description : this.retailerCompany.companyId;
        }
      }
      default: {
        return "";
      }
    }
  }

  public hasLocalInstallerAccess(): boolean {
    // Local installer access was added in version 1.17
    try {
      const split: string[] = this.xpac.firmware.split('.');
      if (Number(split[0]) >= 1 && Number(split[1]) >= 17) {
        return true;
      }
      return false;
    }
    catch (error: any) {
      console.error('Error while parsing XPAC firmware version', error);
      // Fail safe, always show the disclaimer when we are not sure
      return true;
    }
  }

  public getLinkedCompanyDescription(company: Company): string {
    return company.description !== "" ? company.description : company.companyId;
  }

  public removeLinkedCompany(companyToRemove: Company): void {
    if (!this.canEditLinkedCompanies()) {
      return;
    }

    this.linkedCompanies = this.linkedCompanies.filter(company => company.companyId !== companyToRemove.companyId);
    this.formGroupCompanies.markAsDirty();
    this.formGroupCompanies.get("installerCompanies").setValue(this.linkedCompanies, { emitEvent: true });
  }

  public canAddLinkedCompany(): boolean {
    return this.linkedCompanies.length < XpacDetailsComponent.MAX_LINKED_COMPANIES;
  }

  private canEditPartner(): boolean {
    return hasPermission('Device_Companies_Partner', this.permissions, OperationEnum.UPDATE);
  }

  private canEditRetailer(): boolean {
    return hasPermission('Device_Companies_Retailer', this.permissions, OperationEnum.UPDATE);
  }

  public canEditLinkedCompanies(): boolean {
    return hasPermission('Device_Companies_Linked', this.permissions, OperationEnum.UPDATE);
  }

  private canEditInstallationDate(): boolean {
    return hasPermission('Store_InstallationUtc', this.permissions, OperationEnum.UPDATE);
  }

  public canRequestPassword(): boolean {
    return hasPermission('Device_SshPassword', this.permissions, OperationEnum.READ);
  }

  public getPassword(): void {
    if (!this.canRequestPassword() || this.hasPassword || this.gettingPassword) {
      return;
    }

    this.gettingPassword = true;

    this.api.getDevicePassword(this.xpac.deviceId)
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          this.store.dispatch(errorEventAction({
            data: {
              error: error,
              translationkey: 'StoreActionFailed.GetXpacPasswordErrorHasOccurred',
              translationErrorkey: 'GetXpacPasswordStatus',
            }
          }));
          return <string>null;
        })
      )
      .subscribe((password) => {
        if (password) {
          this.hasPassword = true;
          this.formGroupPassword.patchValue({
            password: password,
          });
          this.copyPasswordToClipboard();
          this.cdr.markForCheck();
        }
      });
  }

  public copyPasswordToClipboard(): void {
    const password: string = this.formGroupPassword.getRawValue().password;
    navigator?.clipboard?.writeText(password);
  }

  private initForm(): void {
    this.formGroupXPACInfo = this.formBuilder.group({
      deviceId: [{ value: this.xpac.deviceId, disabled: true }],
      firmware: [{ value: this.xpac.firmware, disabled: true }],
      installationDate: [{ value: toISODateOnly(new Date(this.xpac.installationDateUtc)), disabled: !this.canEditInstallationDate() }],
      shardId: [
        {
          value: this.xpac.shardId === "weu" ? this.translate.instant('Modules.XPACManagementComponent.XpacDetailsComponent.ShardWeu') : this.translate.instant('Modules.XPACManagementComponent.XpacDetailsComponent.ShardWus'),
          disabled: true
        }],
    });
    this.referenceFormGroupXPACInfo = createReference(this.formGroupXPACInfo.value);

    this.formGroupCompanies = this.formBuilder.group({
      dealer: [{ value: this.getDescription(CompanyType.Dealer), disabled: !this.canEditPartner() }, [Validators.required]],
      retailer: [{ value: this.getDescription(CompanyType.Retailer), disabled: !this.canEditRetailer() }],
      installerCompanies: [{ value: this.linkedCompanies, disabled: !this.canEditLinkedCompanies() }],
    });
    this.referenceFormGroupCompanies = createReference(this.formGroupCompanies.value);

    this.formGroupPassword = this.formBuilder.group({
      password: [{ value: '***', disabled: true }],
    });

    this.isLoading = false;

    this.formGroupXPACInfo.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        updateSaveSheetMultiple(
          [this.formGroupXPACInfo, this.formGroupCompanies],
          [this.referenceFormGroupXPACInfo, this.referenceFormGroupCompanies],
          this.saveSheetService);
      });

    this.formGroupCompanies.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        updateSaveSheetMultiple(
          [this.formGroupXPACInfo, this.formGroupCompanies],
          [this.referenceFormGroupXPACInfo, this.referenceFormGroupCompanies],
          this.saveSheetService);
      });

    this.cdr.markForCheck();
  }

  private initListeners(): void {

    this.store.dispatch(getDeviceCompaniesAction({ data: this.xpac.deviceId }));

    this.store.select(selectCompanyInfo(this.xpac.deviceId))
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((xpacCompanyInfo: IXpacCompanyInfoState) => {
        this.xpacCompanyInfo = xpacCompanyInfo.companies;
        this.partnerCompany = this.companyOrNone(this.xpacCompanyInfo.partner, CompanyType.Dealer);
        this.retailerCompany = this.companyOrNone(this.xpacCompanyInfo.retailer, CompanyType.Retailer);
        // Need a copy since we will edit the list
        this.linkedCompanies = [...this.xpacCompanyInfo.linkedCompanies];

        this.retailerCompanyReference = this.companyOrNone(this.xpacCompanyInfo.retailer, CompanyType.Retailer);
        this.partnerCompanyReference = this.companyOrNone(this.xpacCompanyInfo.partner, CompanyType.Dealer);
        this.initForm();
      });

    // On save event
    this.saveSheetService.savedStatus$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe((isSaved: boolean) => {
        if (isSaved) {
          if (this.canEditInstallationDate() && !checkFormPristine(this.formGroupXPACInfo, this.referenceFormGroupXPACInfo)) {
            this.setInstallationDate();
          }
          if (!checkFormPristine(this.formGroupCompanies, this.referenceFormGroupCompanies)) {
            this.setCompanyInfo();
          }
        }
      });

    // Make sure to refresh data when the XPAC installation date has changed
    this.actionsListener$
      .pipe(ofType(DevicesActionsTypes.SET_STORE_INFO_SUCCESS))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.store.dispatch(refreshDevicesAction());
      });
  }

  private setCompanyInfo(): void {

    if (this.retailerCompanyReference.companyId != this.retailerCompany.companyId && this.partnerCompanyReference.companyId !== null) {
      // show warning
      const headerText: string = this.retailerCompany.description === this.translate.instant('Modules.Licenses.List.NoCompanyName') ? this.translate.instant("Modules.Licenses.List.RetailerDetached") : this.translate.instant("Modules.Licenses.List.RetailerChanged");
      const messageText: string = this.retailerCompany.description === this.translate.instant('Modules.Licenses.List.NoCompanyName') ? this.translate.instant("Modules.Licenses.List.RetailerDetachedMessage") : this.translate.instant("Modules.Licenses.List.RetailerChangedMessage");

      const dialogRef: MatDialogRef<MessageDialogComponent> = this.dialog.open(MessageDialogComponent, {
        panelClass: 'messagebox-dialog',
        autoFocus: false,
        data: { headerInfo: headerText, message: messageText, buttons: MessageBoxCustomButton.YesNo }
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.store.dispatch(setDeviceCompaniesAction({
            data: {
              deviceId: this.xpac.deviceId,
              companies: { partner: this.partnerCompany, retailer: this.retailerCompany, linkedCompanies: [...this.linkedCompanies] }
            }
          }));

          this.referenceFormGroupCompanies = createReference(this.formGroupCompanies.value);
          this.retailerCompanyReference = this.retailerCompany;
        }
        else {
          this.saveSheetService.change(true);
        }
      });
    }
    else {
      this.store.dispatch(setDeviceCompaniesAction({
        data: {
          deviceId: this.xpac.deviceId,
          companies: { partner: this.partnerCompany, retailer: this.retailerCompany, linkedCompanies: [...this.linkedCompanies] }
        }
      }));

      this.referenceFormGroupCompanies = createReference(this.formGroupCompanies.value);
    }
  }

  private setInstallationDate(): void {
    this.store.dispatch(setStoreInfoAction({
      data: {
        deviceId: this.xpac.deviceId,
        patchStoreInfo: {
          supervisor: null,
          address: null,
          location: null,
          // Placing it in the middle of the UTC day gives us the biggest chance of getting the correct day in the XPAC's real timezone
          installationUtc: this.formGroupXPACInfo.value.installationDate + 'T12:00:00Z',
        }
      }
    }));

    this.referenceFormGroupXPACInfo = createReference(this.formGroupXPACInfo.value);
  }

  private companyOrNone(company: Company | null, companyType: CompanyType): Company {
    return company ?? {
      companyId: null,
      companyType,
      shardContainerId: '',
      description: companyType === CompanyType.Dealer ? "" : this.translate.instant('Modules.Licenses.List.NoCompanyName'),
      code: '',
    };
  }
}